/*******************************************************************************
 * Copyright (c) 2025 Bula Technologies
 *
 * All rights reserved.
 *
 * Contributors:
 *  James Worthington - Initial implementation
 *  Amina Rais - Massive Improvements
 *  Version: 1.0.0
 *
 *******************************************************************************/
import { useState } from 'react'
import {
  Typography,
  Box,
  Button,
  CircularProgress,
} from '@mui/material'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { TextField } from 'formik-mui'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { makeStyles } from 'tss-react/mui'
import { IMAGES } from '../../../config/assets'
import {
  getUserInfo
} from '../../../store/reducers/user/userAction'
import { useTheme } from '@emotion/react'
import { signInWithEmailAndPassword } from 'firebase/auth'
import { getFirebaseAuth } from '../../../config/firebase/firebaseClient'

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const theme = useTheme();

  const auth = getFirebaseAuth();

  const handleSubmit = async (user, { setSubmitting }) => {
    const { email, password } = user;
    if (!email || !password) return;

    try {
      setLoading(true);
      setError("");

      await signInWithEmailAndPassword(auth, email, password);

      const userInfoAction = await dispatch(getUserInfo());
      const userInfo = userInfoAction.payload;

      if (userInfo && Object.keys(userInfo).length > 0) {
        navigate("/dashboard");
      } else {
        setError("User profile not found. Please contact support.");
      }
    } catch (err) {
      console.error("Login error:", err);

      let message = "Login failed. Please try again.";

      if (err.code) {
        switch (err.code) {
          case "auth/invalid-email":
            message = "Invalid email format.";
            break;
          case "auth/user-disabled":
            message = "This account has been disabled.";
            break;
          case "auth/user-not-found":
          case "auth/wrong-password":
            message = "Incorrect email or password.";
            break;
          case "auth/too-many-requests":
            message =
              "Too many failed attempts. Please wait a moment and try again.";
            break;
          default:
            message = "Incorrect email or password.";
        }
      }

      setError(message);
      setSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <Box className={classes.containerBox}>
      <Box className={classes.backgroundImage} />

      <Box className={classes.BoxContainerLogin}>
        <Link to={'/'}>
          <img src={IMAGES.LOGO} alt='Logo' className={classes.logo} />
        </Link>
        <Typography className={classes.heading}>
          Login To Bula Instant Bets Admin
        </Typography>

        {error && error !== "" && (
          <Typography className={classes.alert}  >
            {error}
          </Typography>
        )}

        <Formik
          initialValues={{
            email: '',
            password: '',
          }}

          validationSchema={Yup.object().shape({
            email: Yup.string()
              .email("Enter a valid email address")
              .required("Email is required"),
            password: Yup.string()
              .min(8, "Password must be at least 8 characters")
              .max(30, "Password must not exceed 30 characters")
              .required("Password is required"),
          })}
          onSubmit={(
            { email, password }, { setSubmitting }
          ) => { handleSubmit({ email, password }, { setSubmitting }) }}

        >
          {({ errors, touched }) => (
            <Form>

              <Field
                name="email"
                label="email"
                type="email"
                component={TextField}
                variant='standard'
                fullWidth
                className={classes.inputField}
                margin='normal'

                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}

              />

              <Field
                name='password'
                label='Password'
                type='password'
                component={TextField}
                variant='standard'
                fullWidth
                className={classes.inputField}
                margin='normal'

                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
              />
              <Button
                variant='contained'
                fullWidth
                type='submit'
                className={classes.button}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color='inherit' /> : 'Login'}
              </Button>
              {process.env.REACT_APP_REGISTER_MODE === "true" &&
                <Box textAlign='center' mt={3}>
                  <Typography
                    variant='body2'
                    style={{ color: '#94a3b8', marginBottom: '8px' }}
                  >
                    Or
                  </Typography>
                  <Button
                    component={Link}
                    fullWidth
                    to='/register'
                    variant='outlined'
                    style={{
                      color: '#fff',
                      borderColor: '#475569',
                      textTransform: 'none',
                      fontWeight: 500,
                      padding: '8px 24px',
                      borderRadius: '8px',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    Sign Up
                  </Button>
                </Box>
              }
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  )
}

const useStyles = makeStyles({ name: 'LoginPage' })(theme => ({
  button: {
    marginTop: theme.spacing(3),
    color: '#fff',
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
    },
    borderRadius: 8,
    height: 50,
    fontWeight: 600,
    fontSize: '1rem',
  },
  containerBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    background: theme.palette.background.paper,
    padding: theme.spacing(2),
  },
  BoxContainerLogin: {
    padding: theme.spacing(4),
    borderRadius: 16,
    backdropFilter: 'blur(15px)',
    backgroundColor: '#332F30',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
    width: '100%',
    maxWidth: 480,
    color: '#f1f5f9',
  },
  backgroundImage: {
    position: 'absolute',
    top: 100,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
    filter: 'grayscale(100%)',
    backgroundImage: `url('/assets/images/bg/bg.svg')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  },
  logo: {
    display: 'block',
    margin: '0 auto 20px auto',
    height: 120,
  },
  heading: {
    textAlign: 'center',
    fontWeight: 700,
    fontSize: '1.8rem',
    marginBottom: theme.spacing(3),
    color: '#e0f2fe',
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.primary.main,
      color: '#e2e8f0',
      '& fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#94a3b8',
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
    },
  },
  alert: {
    marginBottom: theme.spacing(2),
    backgroundColor: '#b91c1c',
    color: '#fef2f2',
  },
}));

export default LoginPage
